import Alert from '@storeblocks/alert';
import Button from '@storeblocks/button';
import Datepicker from '@storeblocks/datepicker';
import Input from '@storeblocks/input/lib';
import Radio from '@storeblocks/radio';
import { add, format, startOfDay } from 'date-fns';
import { Field, Form, Formik, getIn } from 'formik';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import {
  InsuranceDetails,
  postAutomaticInsuranceCancellation,
} from 'Api/insurance';
import { TextArea } from 'Components/FormFields/TextArea/TextArea';
import {
  AlertContainer,
  ButtonContainer,
  FieldsWrapper,
} from 'Pages/CancelInsurancePage/cancelInsurancePage.styled';
import { getAppText } from 'Utils/text';
import { CancellationReason, CancelSurveyReason } from './cancelForm.constants';
import { getCancelSurveyOptions } from './cancelForm.utils';
import { getAutomaticCancellationValidationSchema } from './cancelForm.validation';
import { HealthDeclarationAlert } from './cancelFormComponents/HealthDeclarationAlert';
import { ReconsiderationPrompt } from './cancelFormComponents/ReconsiderationPrompt';

interface FormValues {
  cancelSurvey: {
    reason: CancelSurveyReason | null;
    otherReason: string;
  } | null;
  bankAccount: string | null;
  cancelDate: Date;
}

interface NonRiskInsuranceCancellationFormProps {
  insuranceDetails: InsuranceDetails;
  cancellingLastInsurance: boolean;
}

export const NonRiskInsuranceCancellationForm = ({
  insuranceDetails: {
    policyNumber,
    organizationPillar,
    insuranceProduct,
    agreementProduct,
  },
  cancellingLastInsurance,
}: NonRiskInsuranceCancellationFormProps): React.ReactElement => {
  const [submitError, setSubmitError] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const navigate = useNavigate();

  return (
    <Container>
      {submitSuccess && (
        <AlertContainer>
          <Alert
            variant="success"
            title={getAppText('cancelInsurance.alert.submitSuccess.title')}
            description={getAppText(
              'cancelInsurance.alert.submitSuccess.content',
            )}
          />
        </AlertContainer>
      )}

      {submitError && (
        <AlertContainer>
          <Alert
            variant="error"
            title={getAppText('cancelInsurance.alert.submitError')}
          />
        </AlertContainer>
      )}
      <Formik
        initialValues={{
          cancelSurvey: {
            reason: null,
            otherReason: '',
          },
          bankAccount: null,
          cancelDate: add(new Date(), { days: 1 }),
        }}
        validationSchema={getAutomaticCancellationValidationSchema(
          false,
          cancellingLastInsurance,
        )}
        onSubmit={async ({
          cancelDate: cancelDatevalue,
          bankAccount,
        }: FormValues) => {
          const values = {
            cancelReason: CancellationReason.OBJECT_AT_RISK_NOT_EXIST_ANY_MORE,
            cancelDate: format(cancelDatevalue, 'dd.MM.yyyy'),
            bankAccount,
          };

          try {
            setSubmitError(false);
            await postAutomaticInsuranceCancellation(
              organizationPillar,
              policyNumber,
              values,
            );
            setSubmitSuccess(true);
          } catch {
            setSubmitError(true);
          }
        }}
      >
        {({
          touched,
          errors,
          values,
          setFieldValue,
          isValid,
          isSubmitting,
          submitCount,
        }) => (
          <Form>
            <FieldsWrapper>
              <>
                <Field
                  as={Radio}
                  label={getAppText(
                    'cancelInsurance.cancelSurvey.reason.label',
                  )}
                  id="cancelSurvey.reason"
                  name="cancelSurvey.reason"
                  options={getCancelSurveyOptions()}
                  disabled={submitSuccess}
                  error={
                    getIn(touched, 'cancelSurvey.reason') &&
                    getIn(errors, 'cancelSurvey.reason')
                  }
                />

                {values.cancelSurvey?.reason ===
                  CancelSurveyReason.OTHER_REASON && (
                  <Field
                    className="data-hj-suppress"
                    as={TextArea}
                    fluid
                    label={getAppText(
                      'cancelInsurance.cancelSurvey.otherReason.label',
                    )}
                    name="cancelSurvey.otherReason"
                    disabled={submitSuccess}
                    error={
                      getIn(touched, 'cancelSurvey.otherReason') &&
                      getIn(errors, 'cancelSurvey.otherReason')
                    }
                  />
                )}
              </>

              {values.cancelSurvey?.reason && (
                <>
                  {cancellingLastInsurance && (
                    <Field
                      as={Input}
                      label={getAppText('cancelInsurance.bankAccount.label')}
                      hint={getAppText(
                        'cancelInsurance.bankAccount.description',
                      )}
                      name="bankAccount"
                      disabled={submitSuccess}
                      error={touched.bankAccount && errors.bankAccount}
                    />
                  )}

                  <ReconsiderationPrompt
                    agreementProduct={agreementProduct}
                    cancellationReason={values.cancelSurvey.reason}
                  />

                  <Datepicker
                    id="cancelDate"
                    name="cancelDate"
                    label={getAppText('cancelInsurance.cancelDate.label')}
                    hint={getAppText('cancelInsurance.cancelDate.description')}
                    onChange={(_, s) => setFieldValue('cancelDate', s)}
                    locale="no"
                    format="dd.MM.yyyy"
                    min={add(startOfDay(new Date()), { days: 1 })}
                    max={add(new Date(), { days: 30 })}
                    disabled={submitSuccess}
                    error={touched.cancelDate && (errors.cancelDate as string)}
                    value={values.cancelDate}
                  />

                  <HealthDeclarationAlert agreementProduct={agreementProduct} />
                </>
              )}

              <ButtonContainer>
                {!submitSuccess && (
                  <Button
                    type="submit"
                    data-trackid={`button-questionnaire_cancel_insurance_${insuranceProduct}`}
                    loading={isSubmitting}
                    disabled={submitCount > 0 && !isValid}
                  >
                    {getAppText('cancelInsurance.submit.button.label')}
                  </Button>
                )}

                <Button
                  data-trackid={`button-go_back_cancel_insurance_${insuranceProduct}`}
                  iconLeft="arrow-left"
                  variant="text"
                  onClick={() => navigate(-1)}
                >
                  {getAppText('cancelInsurance.button.goBack')}
                </Button>
              </ButtonContainer>
            </FieldsWrapper>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

const Container = styled.div`
  max-width: 540px;
`;
